import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, Moon } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

const POLL_MS = 20000;
let audioCtx = null;

const getCtx = () => {
  const Ctx = window.AudioContext || window.webkitAudioContext;
  if (!audioCtx && Ctx) { audioCtx = new Ctx(); }
  return audioCtx;
};

const NORMAL = { type: "sine", notes: [880, 1320], step: 0.18, len: 0.45, vol: 0.25 };
const URGENT = { type: "square", notes: [1400, 1000, 1400, 1000, 1400], step: 0.13, len: 0.11, vol: 0.12 };

const playChime = (p = NORMAL) => {
  const ctx = getCtx();
  if (!ctx) { return; }
  if (ctx.state === "suspended") { ctx.resume(); }
  p.notes.forEach((freq, i) => {
    const start = ctx.currentTime + i * p.step;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = p.type;
    osc.frequency.value = freq;
    gain.gain.setValueAtTime(0.0001, start);
    gain.gain.exponentialRampToValueAtTime(p.vol, start + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.0001, start + p.len);
    osc.connect(gain).connect(ctx.destination);
    osc.start(start);
    osc.stop(start + p.len + 0.05);
  });
};

const toMin = (s) => { const [h, m] = s.split(":").map(Number); return h * 60 + m; };

export const inQuietHours = (start, end, now = new Date()) => {
  if (!start || !end) { return false; }
  const s = toMin(start), e = toMin(end), n = now.getHours() * 60 + now.getMinutes();
  if (s === e) { return false; }
  return s < e ? n >= s && n < e : n >= s || n < e;
};

export const NotificationBell = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const { user } = useAuth();
  const baseTitle = useRef(document.title.replace(/^\(\d+\+?\)\s*/, ""));
  const seen = useRef(null);
  const openRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications");
      setItems(data.items);
      setUnread(data.unread_count);
      const ids = new Set(data.items.map((n) => n.notification_id));
      if (seen.current) {
        const fresh = data.items.filter((n) => !n.read && !seen.current.has(n.notification_id));
        const quiet = inQuietHours(user?.quiet_start, user?.quiet_end);
        if (fresh.length && user?.chime_enabled !== false && !quiet) {
          playChime(fresh.some((n) => n.priority === "critical") ? URGENT : NORMAL);
        }
        fresh.forEach((n) => (n.priority === "critical" ? toast.error : toast)(
          n.priority === "critical" ? `CRITICAL: ${n.message}` : n.message, {
            description: n.title,
            duration: 10000,
            ...(n.priority === "critical" ? { className: "!bg-red-600 !text-white !border-red-700 [&_[data-description]]:!text-red-100" } : {}),
            action: { label: "Open", onClick: () => openRef.current(n) },
          }));
      }
      seen.current = ids;
    } catch (e) {
      // ignore transient poll errors
    }
  }, [user?.chime_enabled, user?.quiet_start, user?.quiet_end]);

  useEffect(() => {
    const unlock = () => { const c = getCtx(); if (c && c.state === "suspended") { c.resume(); } };
    window.addEventListener("pointerdown", unlock, { once: true });
    window.addEventListener("keydown", unlock, { once: true });
    return () => {
      window.removeEventListener("pointerdown", unlock);
      window.removeEventListener("keydown", unlock);
    };
  }, []);

  useEffect(() => {
    const base = baseTitle.current;
    document.title = unread > 0 ? `(${unread > 99 ? "99+" : unread}) ${base}` : base;
  }, [unread]);

  useEffect(() => {
    const base = baseTitle.current;
    return () => { document.title = base; };
  }, []);

  const [quietNow, setQuietNow] = useState(false);
  useEffect(() => {
    const check = () => setQuietNow(user?.chime_enabled !== false && inQuietHours(user?.quiet_start, user?.quiet_end));
    check();
    const id = setInterval(check, 30000);
    return () => clearInterval(id);
  }, [user?.chime_enabled, user?.quiet_start, user?.quiet_end]);

  useEffect(() => {
    load();
    const id = setInterval(load, POLL_MS);
    return () => clearInterval(id);
  }, [load]);

  const openItem = async (n) => {
    setOpen(false);
    if (!n.read) {
      await api.post(`/notifications/${n.notification_id}/read`).catch(() => {});
      load();
    }
    navigate(`/tickets/${n.ticket_id}`);
  };
  openRef.current = openItem;

  const markAll = async () => {
    await api.post("/notifications/read-all").catch(() => {});
    load();
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          data-testid="notification-bell"
          data-blinking={unread > 0 ? "true" : "false"}
          aria-label="Notifications"
          title={quietNow ? `Quiet hours ${user.quiet_start}–${user.quiet_end}: chime muted` : "Notifications"}
          className={`relative p-1.5 border rounded-sm transition-colors ${unread > 0 ? "bell-blink border-red-500 text-red-600" : "border-slate-200 text-slate-700 hover:text-red-600 hover:border-red-300"}`}
        >
          <Bell className="h-4 w-4" strokeWidth={1.75} />
          {unread > 0 && (
            <span className="absolute -top-1.5 -right-1.5 h-[18px] w-[18px] rounded-full bg-red-500 opacity-75 animate-ping" />
          )}
          {unread > 0 && (
            <span data-testid="notification-unread-count" className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 rounded-full bg-red-600 text-white text-[10px] font-mono leading-[18px] text-center">
              {unread > 99 ? "99+" : unread}
            </span>
          )}
          {quietNow && (
            <span data-testid="quiet-hours-badge" className="absolute -bottom-1.5 -left-1.5 h-[16px] w-[16px] rounded-full bg-slate-900 text-white flex items-center justify-center ring-2 ring-white">
              <Moon className="h-2.5 w-2.5" strokeWidth={2.5} />
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0 bg-white border-slate-200 rounded-sm" data-testid="notification-panel">
        <div className="flex items-center justify-between px-3 py-2 border-b border-slate-200">
          <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-slate-500">Notifications</span>
          {unread > 0 && (
            <button data-testid="notification-mark-all-btn" onClick={markAll} className="text-[11px] text-red-600 hover:underline">Mark all read</button>
          )}
        </div>
        <div className="max-h-96 overflow-y-auto">
          {items.length === 0 && <div className="px-3 py-6 text-center text-xs text-slate-500" data-testid="notification-empty">No notifications yet</div>}
          {items.map((n) => (
            <button
              key={n.notification_id}
              data-testid={`notification-item-${n.notification_id}`}
              onClick={() => openItem(n)}
              className={`w-full text-left px-3 py-2.5 border-b border-slate-100 hover:bg-slate-50 flex gap-2 ${n.read ? "" : "bg-red-50/60"}`}
            >
              <span className={`mt-1.5 h-2 w-2 rounded-full shrink-0 ${n.read ? "bg-slate-200" : "bg-red-600"}`} />
              <span className="min-w-0">
                <span className="block text-xs text-slate-900">{n.message}</span>
                <span className="block text-[11px] text-slate-500 truncate">{n.title}</span>
                <span className="block text-[10px] font-mono text-slate-400 mt-0.5">{new Date(n.created_at).toLocaleString()}</span>
              </span>
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
};
